// Delivery wording built from the live rules.
//
// Kept apart from pricingEngine.js, which is mirrored byte-for-byte on the
// server — this is presentation only and has no backend twin.
//
// The point of it: before this, pages carried sentences like "ASEAN shipping by
// early 2027" while checkout was already quoting ASEAN at SGD 10. Any claim
// about rates or thresholds should come from the rules, so it cannot drift.

/**
 * A stated delivery time, or null when the client has not given one.
 * Guarding the empty cases matters: Number(null) is 0, which would turn
 * "no time set" into a promise of same-day delivery.
 */
function leadDays(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Zones that can actually be delivered to, cheapest first. */
export function deliveryZones(settings) {
  return ((settings && settings.zones) || [])
    .filter(
      (z) =>
        z.enabled !== false &&
        z.rate !== null &&
        z.rate !== undefined &&
        z.rate !== "",
    )
    .map((z) => ({
      id: z.id,
      label: z.label || z.id,
      rate: Number(z.rate) || 0,
      per: z.rateType === "per_order" ? "per order" : "per copy",
      leadTimeDays: leadDays(z.leadTimeDays),
    }))
    .sort((a, b) => a.rate - b.rate);
}

/**
 * "Deliveries to Singapore take a minimum of 5 working days to fulfil from the
 * date of order." Empty when the client has not set a time for that
 * destination — better to say nothing than to invent a promise.
 */
export function leadTimeSentence(settings, zoneIdOrZone) {
  const zone =
    typeof zoneIdOrZone === "string"
      ? deliveryZones(settings).find((z) => z.id === zoneIdOrZone)
      : zoneIdOrZone;
  if (!zone || zone.leadTimeDays === null) return "";
  const days = zone.leadTimeDays;
  return (
    `Deliveries to ${zone.label} take a minimum of ${days} ` +
    `working ${days === 1 ? "day" : "days"} to fulfil from the date of order.`
  );
}

/** Every destination that has a stated delivery time. */
export function leadTimeSentences(settings) {
  return deliveryZones(settings)
    .filter((z) => z.leadTimeDays !== null)
    .map((z) => leadTimeSentence(settings, z));
}

/** The cheapest rate on offer, for a short "from SGD x" line. */
export function cheapestDelivery(settings) {
  const zones = deliveryZones(settings);
  return zones.length ? zones[0] : null;
}

/** A named zone, for pages that speak about the home market specifically. */
export function zoneById(settings, id) {
  return deliveryZones(settings).find((z) => z.id === id) || null;
}

/** "Singapore (SGD 5.00 per copy), ASEAN (SGD 10.00) and Rest of World (SGD 15.00)" */
export function deliverySentence(settings) {
  const zones = deliveryZones(settings);
  if (zones.length === 0) return "";
  const parts = zones.map(
    (z, i) =>
      `${z.label} (SGD ${z.rate.toFixed(2)}${i === 0 ? ` ${z.per}` : ""})`,
  );
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}
